import { useEffect, useState } from "react";
import { Cross, Plus, Check, RotateCcw, Trash2, Loader2, BookOpen } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

/**
 * Bible-verse memory tracker. Shown only when a student has christian_curriculum = true.
 * - Add a verse (reference + optional text)
 * - Toggle status: learning → memorized (and back)
 * - Delete
 * - Small streak stat: "X memorized this month"
 */
export default function VerseTracker({ studentId }) {
  const [verses, setVerses] = useState([]);
  const [stats, setStats] = useState({ total: 0, memorized: 0, learning: 0, memorized_this_month: 0 });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [reference, setReference] = useState("");
  const [text, setText] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/students/${studentId}/verses`);
      setVerses(data.verses || []);
      setStats(data.stats || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [studentId]);

  const add = async (e) => {
    e.preventDefault();
    if (!reference.trim()) return;
    setAdding(true);
    try {
      await api.post(`/students/${studentId}/verses`, { reference: reference.trim(), text: text.trim() });
      setReference("");
      setText("");
      toast.success("Verse added");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setAdding(false);
    }
  };

  const toggleStatus = async (v) => {
    const next = v.status === "memorized" ? "learning" : "memorized";
    try {
      await api.patch(`/students/${studentId}/verses/${v.id}`, { status: next });
      if (next === "memorized") toast.success(`${v.reference} memorized`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.reference}"?`)) return;
    try {
      await api.delete(`/students/${studentId}/verses/${v.id}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-3xl p-8 shadow-sm" data-testid="verse-tracker">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="grid place-items-center w-10 h-10 rounded-xl bg-indigo-50 text-indigo-700">
            <Cross className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-semibold">Bible-verse memory</h2>
            <p className="text-sm text-stone-500 mt-1">Track verses your student is learning and celebrate each one memorized.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-800 px-3 py-1 text-xs font-semibold ring-1 ring-emerald-200" data-testid="verse-stat-month">
            <BookOpen className="w-3 h-3" /> {stats.memorized_this_month} this month
          </div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 text-stone-700 px-3 py-1 text-xs font-semibold" data-testid="verse-stat-total">
            {stats.memorized}/{stats.total} memorized
          </div>
        </div>
      </div>

      {/* Add form */}
      <form onSubmit={add} className="mt-6 grid gap-3 md:grid-cols-[240px,1fr,auto] md:items-start" data-testid="verse-add-form">
        <Input
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="John 3:16"
          className="h-11 rounded-xl"
          required
          data-testid="verse-reference-input"
        />
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Optional: the verse text (helps with review)"
          rows={1}
          className="rounded-xl min-h-11"
          data-testid="verse-text-input"
        />
        <Button
          type="submit"
          disabled={adding || !reference.trim()}
          className="rounded-full bg-indigo-500 hover:bg-indigo-600 text-white h-11 px-5"
          data-testid="verse-add-btn"
        >
          {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          Add verse
        </Button>
      </form>

      {/* List */}
      <div className="mt-6 space-y-2" data-testid="verse-list">
        {loading && (
          <div className="flex items-center gap-2 text-stone-500 text-sm py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading verses…
          </div>
        )}
        {!loading && verses.length === 0 && (
          <div className="text-stone-500 text-sm text-center py-8 border border-dashed border-stone-200 rounded-2xl" data-testid="verse-empty">
            No verses yet. Add the first one above.
          </div>
        )}
        {verses.map((v) => {
          const memorized = v.status === "memorized";
          return (
            <div
              key={v.id}
              className={`flex items-start justify-between gap-3 p-4 rounded-2xl border transition-colors ${
                memorized ? "bg-emerald-50/50 border-emerald-200" : "bg-white border-stone-200"
              }`}
              data-testid={`verse-item-${v.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-semibold text-stone-900">{v.reference}</span>
                  <span className={`text-[10px] uppercase tracking-[0.15em] font-semibold rounded-full px-2 py-0.5 ${
                    memorized ? "bg-emerald-500 text-white" : "bg-amber-100 text-amber-800"
                  }`} data-testid={`verse-status-${v.id}`}>
                    {memorized ? "Memorized" : "Learning"}
                  </span>
                </div>
                {v.text && <p className="mt-1.5 text-sm text-stone-600 leading-relaxed">{v.text}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => toggleStatus(v)}
                  className={`rounded-full h-8 px-3 text-xs ${memorized ? "text-stone-500 hover:text-stone-800" : "text-emerald-700 hover:bg-emerald-50"}`}
                  data-testid={`verse-toggle-${v.id}`}
                >
                  {memorized ? (<><RotateCcw className="w-3 h-3 mr-1" /> Reset</>) : (<><Check className="w-3 h-3 mr-1" /> Mark memorized</>)}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(v)}
                  className="rounded-full h-8 w-8 p-0 text-stone-400 hover:text-red-600 hover:bg-red-50"
                  data-testid={`verse-delete-${v.id}`}
                  aria-label={`Delete ${v.reference}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
